import { useState, useEffect, useRef, useCallback } from 'react';
import type { Editor } from '@tiptap/react';
import {
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListChecks,
  Code2,
  Quote,
  Image,
  Minus,
  Info,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Type,
} from 'lucide-react';

interface SlashMenuItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  command: (editor: Editor) => void;
}

const MENU_ITEMS: SlashMenuItem[] = [
  {
    title: 'Paragraph',
    description: 'Plain text block',
    icon: <Type className="h-4 w-4" />,
    command: (editor) => editor.chain().focus().setParagraph().run(),
  },
  {
    title: 'Heading 1',
    description: 'Large section heading',
    icon: <Heading1 className="h-4 w-4" />,
    command: (editor) => editor.chain().focus().setHeading({ level: 1 }).run(),
  },
  {
    title: 'Heading 2',
    description: 'Medium section heading',
    icon: <Heading2 className="h-4 w-4" />,
    command: (editor) => editor.chain().focus().setHeading({ level: 2 }).run(),
  },
  {
    title: 'Heading 3',
    description: 'Small section heading',
    icon: <Heading3 className="h-4 w-4" />,
    command: (editor) => editor.chain().focus().setHeading({ level: 3 }).run(),
  },
  {
    title: 'Bullet List',
    description: 'Unordered list',
    icon: <List className="h-4 w-4" />,
    command: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    title: 'Ordered List',
    description: 'Numbered list',
    icon: <ListOrdered className="h-4 w-4" />,
    command: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    title: 'Task List',
    description: 'Checklist items',
    icon: <ListChecks className="h-4 w-4" />,
    command: (editor) => editor.chain().focus().toggleTaskList().run(),
  },
  {
    title: 'Code Block',
    description: 'Syntax highlighted code',
    icon: <Code2 className="h-4 w-4" />,
    command: (editor) => editor.chain().focus().toggleCodeBlock().run(),
  },
  {
    title: 'Quote',
    description: 'Block quotation',
    icon: <Quote className="h-4 w-4" />,
    command: (editor) => editor.chain().focus().toggleBlockquote().run(),
  },
  {
    title: 'Image',
    description: 'Upload or paste image URL',
    icon: <Image className="h-4 w-4" />,
    command: (editor) => {
      const url = window.prompt('Image URL:');
      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    },
  },
  {
    title: 'Divider',
    description: 'Horizontal rule',
    icon: <Minus className="h-4 w-4" />,
    command: (editor) => editor.chain().focus().setHorizontalRule().run(),
  },
  {
    title: 'Info Callout',
    description: 'Informational hint',
    icon: <Info className="h-4 w-4" />,
    command: (editor) => {
      editor.chain().focus().insertContent({
        type: 'callout',
        attrs: { type: 'info' },
        content: [{ type: 'paragraph' }],
      }).run();
    },
  },
  {
    title: 'Warning Callout',
    description: 'Warning hint',
    icon: <AlertTriangle className="h-4 w-4" />,
    command: (editor) => {
      editor.chain().focus().insertContent({
        type: 'callout',
        attrs: { type: 'warning' },
        content: [{ type: 'paragraph' }],
      }).run();
    },
  },
  {
    title: 'Success Callout',
    description: 'Success hint',
    icon: <CheckCircle2 className="h-4 w-4" />,
    command: (editor) => {
      editor.chain().focus().insertContent({
        type: 'callout',
        attrs: { type: 'success' },
        content: [{ type: 'paragraph' }],
      }).run();
    },
  },
  {
    title: 'Danger Callout',
    description: 'Danger/error hint',
    icon: <XCircle className="h-4 w-4" />,
    command: (editor) => {
      editor.chain().focus().insertContent({
        type: 'callout',
        attrs: { type: 'danger' },
        content: [{ type: 'paragraph' }],
      }).run();
    },
  },
];

interface SlashCommandMenuProps {
  editor: Editor;
  isOpen: boolean;
  onClose: () => void;
  position: { top: number; left: number };
  query: string;
}

export function SlashCommandMenu({ editor, isOpen, onClose, position, query }: SlashCommandMenuProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  const filteredItems = MENU_ITEMS.filter(item =>
    item.title.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const executeCommand = useCallback((item: SlashMenuItem) => {
    // Delete the slash and query text
    const { state } = editor;
    const { from } = state.selection;
    const textBefore = state.doc.textBetween(
      Math.max(0, from - query.length - 1),
      from,
      '\n'
    );
    const slashPos = textBefore.lastIndexOf('/');
    if (slashPos >= 0) {
      const deleteFrom = from - query.length - 1;
      editor.chain().deleteRange({ from: deleteFrom, to: from }).run();
    }

    item.command(editor);
    onClose();
  }, [editor, query, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredItems.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredItems.length) % filteredItems.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredItems[selectedIndex]) {
          executeCommand(filteredItems[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, selectedIndex, filteredItems, executeCommand, onClose]);

  if (!isOpen || filteredItems.length === 0) return null;

  return (
    <div
      ref={menuRef}
      className="fixed z-50 w-72 max-h-80 overflow-y-auto rounded-lg border border-border bg-popover p-1.5 shadow-lg"
      style={{ top: position.top, left: position.left }}
    >
      {filteredItems.map((item, index) => (
        <button
          key={item.title}
          className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors ${
            index === selectedIndex
              ? 'bg-accent text-accent-foreground'
              : 'text-popover-foreground hover:bg-accent/50'
          }`}
          onClick={() => executeCommand(item)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-background">
            {item.icon}
          </span>
          <div>
            <div className="font-medium">{item.title}</div>
            <div className="text-xs text-muted-foreground">{item.description}</div>
          </div>
        </button>
      ))}
    </div>
  );
}
